import { Link } from 'react-router-dom'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { Calendar } from 'lucide-react'

import {
    DataTableColumn,
    FilterItemConfig,
    FilterToggle,
} from '@/components/ui/data-table-customized'
import { Badge } from '@/components/ui/badge'
import { Process } from '../../types/response'
import { paths } from '@/utils/path'
import { getProcessTypeLabel } from '../../utils'
import {
    PROCESS_TYPE_LABELS,
    PROCESS_STATUS,
    PROCESS_STATUS_VARIANTS,
} from '../../constants'

dayjs.extend(relativeTime)

// === Columns ===

export const getProcessColumns = (): DataTableColumn<Process>[] => [
    {
        key: 'code',
        title: 'Process Code',
        width: 160,
        pinned: 'left',
        render: (value, row) => (
            <Link
                to={`${paths.warehouseProcesses}/${row.id}`}
                className="text-blue-600 hover:underline font-medium"
            >
                {value}
            </Link>
        ),
    },
    {
        key: 'type',
        title: 'Type',
        width: 160,
        render: (_value, row) => (
            <Badge variant="outline">{getProcessTypeLabel(row.type)}</Badge>
        ),
    },
    {
        key: 'status',
        title: 'Status',
        width: 140,
        render: (_value, row) => {
            const variant =
                (PROCESS_STATUS_VARIANTS[row.status] as
                    | 'outline'
                    | 'secondary'
                    | 'default'
                    | 'destructive') || 'outline'
            return (
                <Badge variant={variant} className="capitalize">
                    {row.status?.replace('_', ' ')}
                </Badge>
            )
        },
    },
    {
        key: 'source',
        title: 'From',
        width: 180,
        render: (_value, row) => (
            <span className="text-sm">{row.source?.name || '-'}</span>
        ),
    },
    {
        key: 'destination',
        title: 'To',
        width: 180,
        render: (_value, row) => (
            <span className="text-sm">{row.destination?.name || '-'}</span>
        ),
    },
    {
        key: 'reason',
        title: 'Reason',
        width: 200,
        render: (value) => (
            <span className="text-sm truncate block max-w-[200px]">
                {(value as string) || '-'}
            </span>
        ),
    },
    {
        key: 'created_at',
        title: 'Created',
        width: 150,
        render: (value) => {
            const date = dayjs(value as string)
            return (
                <div
                    className="flex items-center gap-2"
                    title={date.format('YYYY-MM-DD HH:mm:ss')}
                >
                    <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                    <span className="text-sm">{date.fromNow()}</span>
                </div>
            )
        },
    },
]

// === Filters ===

const processTypeOptions = Object.entries(PROCESS_TYPE_LABELS)
    .filter(([key]) =>
        [
            'import_purchase',
            'import_return',
            'export_sale',
            'export_loan',
            'export_delivery',
            'internal_transfer',
            'inventory_check',
        ].includes(key)
    )
    .map(([value, label]) => ({ value, label }))

const processStatusOptions = Object.entries(PROCESS_STATUS).map(
    ([_key, value]) => ({
        value,
        label: value
            .split('_')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
    })
)

export const getProcessFilterItems = (): FilterItemConfig[] => [
    {
        type: 'select',
        name: 'type',
        label: 'Type',
        placeholder: 'Filter by type',
        options: processTypeOptions,
    },
    {
        type: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Filter by status',
        options: processStatusOptions,
    },
]

// === Filter Toggles (column visibility) ===

export const getProcessFilterToggles = (): FilterToggle[] => [
    { key: 'code', label: 'Process Code' },
    { key: 'type', label: 'Type' },
    { key: 'status', label: 'Status' },
    { key: 'source', label: 'From' },
    { key: 'destination', label: 'To' },
    { key: 'reason', label: 'Reason' },
    { key: 'created_at', label: 'Created' },
]
